import React, { useState } from "react";
import { X } from "lucide-react";
import { VbButton, CompetitorFavicon, competitorHref, Input } from "@/components/vb";
import type { CompetitorDraft } from "./useBuyerWizard";
import { MAX_COMPETITORS } from "./useBuyerWizard";

type Props = {
  competitors: CompetitorDraft[];
  onAdd: (raw: string) => void;
  onRemove: (c: CompetitorDraft) => void;
  error: string | null;
  /** A competitor add is in flight - disable the control to avoid double-POSTs. */
  busy?: boolean;
};

/** Per-buyer competitor list: a domain in → {name,url} tag out, ≤5. Mirrors the
 *  prototype's competitors field panel. */
export function CompetitorEditor({ competitors, onAdd, onRemove, error, busy }: Props) {
  const [draft, setDraft] = useState("");
  const atCap = competitors.length >= MAX_COMPETITORS;
  const disabled = atCap || !!busy;

  const submit = () => {
    const value = draft.trim();
    if (!value || disabled) return;
    onAdd(value);
    setDraft("");
  };

  return (
    <div className="comp-add-row">
      {competitors.length > 0 && (
        <div className="comp-tags">
          {competitors.map((c) => (
            <span className="comp-tag" key={c.url}>
              {/* The badge (favicon + name + url) is a link to the competitor's
                  site; the X is a sibling, so removing still only removes. */}
              <a
                className="comp-tag-link"
                href={competitorHref(c.url)}
                target="_blank"
                rel="noopener noreferrer"
                title={`Open ${c.url.replace(/^https?:\/\//, "")}`}
              >
                <CompetitorFavicon name={c.name} url={c.url} size={16} />
                <span className="cn">{c.name}</span>
                <span className="cu">{c.url.replace(/^https?:\/\//, "")}</span>
              </a>
              <button
                type="button"
                className="x"
                onClick={() => onRemove(c)}
                aria-label={`Remove ${c.name}`}
              >
                <X size={13} aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="own-row">
        <Input
          size="lg"
          type="text"
          value={draft}
          placeholder="Add a competitor domain…"
          disabled={disabled}
          maxLength={200}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          aria-label="Competitor domain"
        />
        <VbButton type="button" variant="secondary" onClick={submit} disabled={disabled}>
          Add
        </VbButton>
      </div>
      {error && <div className="limit-flash show">{error}</div>}
      <span className="help">
        {atCap
          ? `You've added the maximum of ${MAX_COMPETITORS} competitors.`
          : "AI compares your company against these. We infer the name from the domain."}
      </span>
    </div>
  );
}

export default CompetitorEditor;
